// Import API response types
import {
  BulkUpdateUserPermissionsRequest as BulkUpdateRequest,
  BulkUpdateUserPermissionsResponse as BulkUpdateResponse,
  CreateRoleRequest,
  Permission,
  PermissionStatistics,
  GetPermissionStatisticsResponse as PermissionStatisticsResponse,
  Role,
  GetRoleHierarchyResponse as RoleHierarchyResponse,
  UpdateRoleRequest,
  UserCreateFormData,
  UserData,
  GetUserPermissionsResponse as UserPermissionsResponse,
  GetUsersWithPermissionResponse as UsersWithPermissionResponse,
  UserUpdateFormData,
} from './api-responses';

// Re-export commonly used API types for convenience
export type {
  BulkUpdateRequest,
  BulkUpdateResponse,
  CreateRoleRequest,
  Permission,
  PermissionStatistics,
  PermissionStatisticsResponse,
  Role,
  RoleHierarchyResponse,
  UpdateRoleRequest,
  UserCreateFormData,
  UserData,
  UserPermissionsResponse,
  UsersWithPermissionResponse,
  UserUpdateFormData,
};

// Sheet-specific types extending API types
export interface User extends Omit<UserData, 'permissions'> {
  permissions?: {
    [key: string]: string[];
  };
}

export interface RoleFormData {
  name: string;
  description: string;
  permissions: {
    [resource: string]: string[];
  };
}

// Resource permissions configuration
export interface ResourcePermissionsConfig {
  [resource: string]: string[];
}

export const RESOURCE_PERMISSIONS: ResourcePermissionsConfig = {
  // Sales Group
  sales_order: ['read', 'write', 'update', 'delete', 'approve'],
  customers: ['read', 'write', 'update', 'delete', 'approve'],
  purchase_order: ['read', 'write', 'update', 'delete', 'approve'],
  discounts: ['read', 'write', 'update', 'delete', 'approve'],
  transactions: ['read', 'write', 'update', 'delete'],

  // Inventory Group
  inventory: ['read', 'write', 'update', 'delete', 'approve'],
  requisitions: ['read', 'write', 'update', 'delete', 'approve'], // Purchase Requisitions
  suppliers: ['read', 'write', 'update', 'delete', 'approve'],
  regions: ['read', 'write', 'update', 'delete', 'approve'], // Sourcing Regions

  // Administration
  users: ['read', 'write', 'update', 'delete'],
  reports: ['read', 'write', 'update', 'delete'],
  notifications: ['read', 'write'], // Todo: make this a default select
  audit: ['read'],

  // System
  dashboard: ['read'], // Todo: make this a default select
};

// Sheet component props
export interface UserCreateSheetProps {
  roles: Role[];
  onSuccess?: () => void;
  onCancel?: () => void;
}

export interface UserUpdateSheetProps {
  userData: User;
  roles: Role[];
  onSuccess?: () => void;
  onCancel?: () => void;
}

export interface RoleCreateSheetProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

export interface RoleUpdateSheetProps {
  roleData: Role;
  onSuccess?: () => void;
  onCancel?: () => void;
}

// Permission display names
export const PERMISSION_DISPLAY_NAMES: { [key: string]: string } = {
  read: 'Read',
  write: 'Write',
  update: 'Update',
  delete: 'Delete',
  approve: 'Approve',
  generate: 'Generate',
  export: 'Export',
};

// API response types
export interface ApiResponse<T = any> {
  success: boolean;
  status: boolean;
  message: string;
  data?: T;
}

export interface RoleApiResponse extends ApiResponse {
  data: {
    roles: Role[];
  };
}

export interface UserApiResponse extends ApiResponse {
  data: {
    user: User;
  };
}
